/**
 * Firestore CRUD for drawing markup on PDFs and images (documentMarkup collection).
 */

import { db } from './firebase';
import { collection, addDoc, query, where, onSnapshot, deleteDoc, doc, updateDoc, Timestamp } from 'firebase/firestore';
import type { DocumentMarkupPoint, DocumentMarkupStroke } from '../types';

const COLLECTION = 'documentMarkup';

function fromFirestore(docSnap: { id: string; data: () => Record<string, unknown> }): DocumentMarkupStroke {
  const d = docSnap.data();
  const points = (d.points as DocumentMarkupPoint[]) || [];
  return {
    id: docSnap.id,
    documentId: d.documentId as string,
    documentUrl: d.documentUrl as string,
    leadId: d.leadId as string,
    pageNumber: d.pageNumber as number,
    tool: d.tool as DocumentMarkupStroke['tool'],
    color: (d.color as string) || '#3b82f6',
    strokeWidth: typeof d.strokeWidth === 'number' ? d.strokeWidth : 1.2,
    points,
    userId: d.userId as string,
    userName: d.userName as string,
    projectId: (d.projectId as string) || undefined,
    text: typeof d.text === 'string' ? d.text : undefined,
    fill: typeof d.fill === 'string' ? d.fill : undefined,
    textColor: typeof d.textColor === 'string' ? d.textColor : undefined,
    stampId: typeof d.stampId === 'string' ? d.stampId : undefined,
    stampScale: typeof d.stampScale === 'number' ? d.stampScale : undefined,
    stampRotation: typeof d.stampRotation === 'number' ? d.stampRotation : undefined,
    createdAt: (d.createdAt as { toDate: () => Date })?.toDate?.() ?? new Date(),
  };
}

function optionalMarkupFields(stroke: Partial<DocumentMarkupStroke>): Record<string, unknown> {
  const extra: Record<string, unknown> = {};
  if (stroke.text != null) extra.text = stroke.text;
  if (stroke.fill) extra.fill = stroke.fill;
  if (stroke.textColor) extra.textColor = stroke.textColor;
  if (stroke.stampId) extra.stampId = stroke.stampId;
  if (typeof stroke.stampScale === 'number') extra.stampScale = stroke.stampScale;
  if (typeof stroke.stampRotation === 'number') extra.stampRotation = stroke.stampRotation;
  return extra;
}

export async function createMarkupStroke(stroke: Omit<DocumentMarkupStroke, 'id'>): Promise<string> {
  const docData: Record<string, unknown> = {
    documentId: stroke.documentId,
    documentUrl: stroke.documentUrl,
    leadId: stroke.leadId,
    pageNumber: stroke.pageNumber,
    tool: stroke.tool,
    color: stroke.color,
    strokeWidth: stroke.strokeWidth,
    points: stroke.points,
    userId: stroke.userId,
    userName: stroke.userName,
    createdAt: Timestamp.fromDate(stroke.createdAt),
    ...optionalMarkupFields(stroke),
  };
  if (stroke.projectId) docData.projectId = stroke.projectId;
  const ref = await addDoc(collection(db, COLLECTION), docData);
  return ref.id;
}

export async function updateMarkupStroke(
  strokeId: string,
  patch: Partial<Pick<DocumentMarkupStroke, 'text' | 'points' | 'stampRotation' | 'stampScale'>>
): Promise<void> {
  const data: { text?: string; points?: DocumentMarkupPoint[]; stampRotation?: number; stampScale?: number } = {};
  if (patch.text != null) data.text = patch.text;
  if (patch.points) data.points = patch.points;
  if (typeof patch.stampRotation === 'number') data.stampRotation = patch.stampRotation;
  if (typeof patch.stampScale === 'number') data.stampScale = patch.stampScale;
  if (Object.keys(data).length === 0) return;
  await updateDoc(doc(db, COLLECTION, strokeId), data);
}

export async function deleteMarkupStroke(strokeId: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTION, strokeId));
}

/** Subscribe to markup for a document (merged by documentId and optionally documentUrl). */
export function subscribeToMarkupForDocument(
  documentId: string,
  documentUrl: string,
  callback: (strokes: DocumentMarkupStroke[]) => void
): () => void {
  const unsubs: (() => void)[] = [];
  let byId: DocumentMarkupStroke[] = [];
  let byUrl: DocumentMarkupStroke[] = [];
  const mergeAndSort = () => {
    const seen = new Set<string>();
    const merged: DocumentMarkupStroke[] = [];
    for (const s of [...byId, ...byUrl]) {
      if (s.id && !seen.has(s.id)) {
        seen.add(s.id);
        merged.push(s);
      } else if (!s.id) merged.push(s);
    }
    callback(merged.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime()));
  };
  unsubs.push(
    onSnapshot(query(collection(db, COLLECTION), where('documentId', '==', documentId)), snapshot => {
      byId = snapshot.docs.map(d => fromFirestore({ id: d.id, data: () => d.data() }));
      mergeAndSort();
    })
  );
  if (documentUrl && documentUrl !== documentId) {
    unsubs.push(
      onSnapshot(query(collection(db, COLLECTION), where('documentUrl', '==', documentUrl)), snapshot => {
        byUrl = snapshot.docs.map(d => fromFirestore({ id: d.id, data: () => d.data() }));
        mergeAndSort();
      })
    );
  }
  return () => unsubs.forEach(u => u());
}
